export type ColumnVisibilityState = Record<string, boolean>;

export type HubspotColumnKey =
  | 'dapp'
  | 'status'
  | 'checks'
  | 'assignee'
  | 'createdAt'
  | 'ticket'
  | 'version'
  | 'addresses'
  | 'closedDate'
  | 'lastModifiedAt';

const hubspotColumnKeys: HubspotColumnKey[] = [
  'dapp',
  'status',
  'checks',
  'assignee',
  'createdAt',
  'ticket',
  'version',
  'addresses',
  'closedDate',
  'lastModifiedAt',
];

// Column keys double as API sort ids; these two have no server-side sort.
export type HubspotSortableColumnKey = Exclude<
  HubspotColumnKey,
  'assignee' | 'checks'
>;

export const hubspotSortableColumnKeys = hubspotColumnKeys.filter(
  (key): key is HubspotSortableColumnKey =>
    key !== 'assignee' && key !== 'checks',
);

// v2: the column set changed with the queue redesign, so stale v1 prefs are ignored.
export const COLUMN_VISIBILITY_STORAGE_KEY =
  'hubspot_tickets_column_visibility_v2';

export const columnVisibilityOptions: Array<{
  key: HubspotColumnKey;
  label: string;
  defaultVisible: boolean;
  canHide: boolean;
}> = [
  { key: 'dapp', label: 'Submission', defaultVisible: true, canHide: false },
  { key: 'status', label: 'Status', defaultVisible: true, canHide: true },
  { key: 'checks', label: 'Checks', defaultVisible: true, canHide: true },
  { key: 'assignee', label: 'Assignee', defaultVisible: true, canHide: true },
  { key: 'createdAt', label: 'Created', defaultVisible: true, canHide: true },
  { key: 'ticket', label: 'Ticket ID', defaultVisible: false, canHide: true },
  {
    key: 'version',
    label: 'Version / Size',
    defaultVisible: false,
    canHide: true,
  },
  {
    key: 'addresses',
    label: 'NFT Addresses',
    defaultVisible: false,
    canHide: true,
  },
  { key: 'closedDate', label: 'Closed', defaultVisible: false, canHide: true },
  {
    key: 'lastModifiedAt',
    label: 'Last Modified',
    defaultVisible: false,
    canHide: true,
  },
];

const defaultColumnVisibility = columnVisibilityOptions.reduce(
  (acc, option) => {
    acc[option.key] = option.defaultVisible;
    return acc;
  },
  {} as ColumnVisibilityState,
);

export function normalizeColumnVisibility(raw: unknown): ColumnVisibilityState {
  const visibility: ColumnVisibilityState = { ...defaultColumnVisibility };
  const stored =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;

  for (const option of columnVisibilityOptions) {
    if (!option.canHide) {
      visibility[option.key] = true;
      continue;
    }

    const storedValue = stored?.[option.key];
    if (typeof storedValue === 'boolean') {
      visibility[option.key] = storedValue;
    }
  }

  return visibility;
}
